/**
 * AR Vision Demo — seed dataset.
 *
 * THROWAWAY DEMO CODE. Builds the initial `ArDemoFixtureState` snapshot for the
 * storyline. Everything is hardcoded and deterministic. Swap the placeholder
 * brand by editing `BRAND` below (single source of truth).
 *
 * Dates are computed relative to "now" so the aging picture (e.g. the
 * 4-weeks-overdue customer) stays correct whenever the demo runs.
 */

import type {
  ArCustomerCommsSummary,
  ArCustomerProfileResponse,
  ArInvoiceListItem,
  CollectionThreadWithActivitiesDto,
  GetArAgingResponse,
  GetArCustomAgingResponse,
  GetArInvoiceResponse,
  GetArSummaryResponse,
  InboxCustomerSummaryDto,
  ListCollectionThreadEvidenceOutput,
  RankedCollectionThreadDto,
} from '@erp/rpc';
import type {
  ArDemoFixtureState,
  DemoAgentAction,
  DemoReminderEmail,
  DemoSalesRepEmailDraft,
} from './fixture-store.types';
// AR Technical Demo (F0 / GEN-2256) — the Crestline storyline, layered on top.
import {
  buildTechnicalSeed,
  buildDocumentPreview,
} from './fixture-seed-technical';
// Blanket cash-receipt ingestion — the "process today's cash receipts" run.
import {
  buildCashReceiptIngestionSeed,
  buildCashReceiptRemittanceRecords,
} from './fixture-seed-cash-receipts';
// Bulk statement-send demo — the 25-customer "approve payment reminders" batch.
import {
  appendBulkToAging,
  appendBulkToCustomAging,
  BULK_STATEMENT_ACTION_ID,
  buildBulkStatementSeed,
  bulkStatementBatchTotal,
} from './fixture-seed-bulk-statements';
// AR Email Intelligence Demo (E0 / GEN-2262) — additive, isolated seed.
import { buildEmailIntelSeed } from './email-intel/email-intel-seed';
import { buildCommsNotesSeed } from './fixture-seed-comms-notes';

/** Single source of truth for the placeholder brand. Swap here to rebrand. */
export const BRAND = {
  flagshipCustomerName: 'Harbor Foods Co.',
  overdueCustomerName: 'Cascade Manufacturing Co.',
  disputeCustomerName: 'Granite Peak Distributors',
  /** Report-only customer surfaced in the Custom Aging Report exploration beat. */
  reportCustomerName: 'Cedar Ridge Foods',
  /** Path under /public for the branded invoice/statement letterhead asset. */
  letterheadUrl: '/demo/ar/letterhead.svg',
  /** Plain statement — the first preview Nami builds in scene 5. */
  plainStatementUrl: '/demo/ar/statement-plain.svg',
  /** Overdue lines bold + highlighted yellow — applied by the scene 5b beat. */
  highlightedStatementUrl: '/demo/ar/statement-highlighted.svg',
  bolUrl: '/demo/ar/bol-pillar.svg',
} as const;

// Stable UUID-ish ids (demo only; do not need to be real DB ids).
const ID = {
  flagshipCustomer: 'demo-cust-pillar-0001',
  overdueCustomer: 'demo-cust-cascade-0002',
  disputeCustomer: 'demo-cust-granite-0003',
  flagshipInvoice: 'demo-inv-pillar-0001',
  flagshipSalesOrder: 'demo-so-pillar-0001',
  disputeThread: 'demo-thread-granite-0001',
  overdueThread: 'demo-thread-cascade-0001',
  reportCustomer: 'demo-cust-cedar-0004',
} as const;

/**
 * The report-only customer, exported so the scripted report tools + the
 * sales-rep email panel can target it without threading it through the store.
 */
export const REPORT_CUSTOMER = {
  customerId: ID.reportCustomer,
  customerName: BRAND.reportCustomerName,
} as const;

/** Our own internal sales rep on the Cedar Ridge account (the email recipient). */
const REPORT_SALES_REP = {
  contactId: 'demo-rep-3',
  name: 'Renee Ostrander',
  email: 'renee.ostrander@gently-demo.com',
} as const;

const INVOICE_NUMBER = 'INV-48217';

function isoDaysFromNow(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString();
}

function dateOnlyDaysFromNow(days: number): string {
  return isoDaysFromNow(days).slice(0, 10);
}

const now = new Date().toISOString();

// ---------------------------------------------------------------------------
// AR summary (scene 4)
// ---------------------------------------------------------------------------
const summary: GetArSummaryResponse = {
  totalOutstanding: '1284560.00',
  overdueAmount: '342180.00',
  totalInvoiceCount: 1463,
  openInvoiceCount: 188,
  overdueInvoiceCount: 41,
  dso: 38,
  incomingPaymentsTotal: '96400.00',
  incomingPaymentsCount: 7,
};

// ---------------------------------------------------------------------------
// Aging (scene 4) — includes the 4-weeks-overdue and dispute customers
// ---------------------------------------------------------------------------
const bucketDefinitions: GetArAgingResponse['bucketDefinitions'] = [
  { label: 'Current', minDays: 0, maxDays: 0 },
  { label: '1-30', minDays: 1, maxDays: 30 },
  { label: '31-60', minDays: 31, maxDays: 60 },
  { label: '61-90', minDays: 61, maxDays: 90 },
  { label: '90+', minDays: 91 },
];

const aging: GetArAgingResponse = {
  customers: [
    {
      customerId: ID.flagshipCustomer,
      customerName: BRAND.flagshipCustomerName,
      current: '412800.00',
      days1_30: '0.00',
      days31_60: '0.00',
      days61_90: '0.00',
      days90plus: '0.00',
      bucketAmounts: ['412800.00', '0.00', '0.00', '0.00', '0.00'],
      totalOutstanding: '412800.00',
      totalApplied: '0.00',
      invoiceCount: 3,
    },
    {
      customerId: ID.overdueCustomer,
      customerName: BRAND.overdueCustomerName,
      current: '0.00',
      days1_30: '128400.00',
      days31_60: '0.00',
      days61_90: '0.00',
      days90plus: '0.00',
      bucketAmounts: ['0.00', '128400.00', '0.00', '0.00', '0.00'],
      totalOutstanding: '128400.00',
      totalApplied: '0.00',
      invoiceCount: 2,
    },
    {
      customerId: ID.disputeCustomer,
      customerName: BRAND.disputeCustomerName,
      current: '0.00',
      days1_30: '0.00',
      days31_60: '43250.00',
      days61_90: '0.00',
      days90plus: '0.00',
      bucketAmounts: ['0.00', '0.00', '43250.00', '0.00', '0.00'],
      totalOutstanding: '43250.00',
      totalApplied: '43250.00',
      invoiceCount: 1,
    },
  ],
  totals: {
    current: '412800.00',
    days1_30: '128400.00',
    days31_60: '43250.00',
    days61_90: '0.00',
    days90plus: '0.00',
    bucketAmounts: ['412800.00', '128400.00', '43250.00', '0.00', '0.00'],
    totalOutstanding: '584450.00',
    totalApplied: '43250.00',
  },
  bucketDefinitions,
};

// ---------------------------------------------------------------------------
// Flagship invoice (scenes 1-2)
// ---------------------------------------------------------------------------
const flagshipInvoice: NonNullable<GetArInvoiceResponse['invoice']> = {
  id: ID.flagshipInvoice,
  invoiceNumber: INVOICE_NUMBER,
  invoiceDate: dateOnlyDaysFromNow(0),
  dueDate: dateOnlyDaysFromNow(30),
  status: 'open',
  grossAmount: '137600.00',
  netAmount: '137600.00',
  balanceDue: '137600.00',
  currencyCode: 'USD',
  customerName: BRAND.flagshipCustomerName,
  customerId: ID.flagshipCustomer,
  customerPurchaseOrderReference: 'PO-PIL-99214',
  salesOrderId: ID.flagshipSalesOrder,
  shipDate: dateOnlyDaysFromNow(-1),
  paymentApplicationCount: 0,
  firstSentAt: now,
  sendEvents: [
    {
      occurredAt: now,
      note: `Invoice ${INVOICE_NUMBER} emailed to ap@harborfoods.com`,
    },
  ],
  customerCode: 'PILLR',
  totalPaid: '0.00',
  paymentTermDays: 30,
  paymentTermsDescription: 'Net 30',
  isSent: true,
  originSystem: 'demo',
  originId: ID.flagshipSalesOrder,
  originEntity: 'sales_order',
  salesOrder: {
    id: ID.flagshipSalesOrder,
    customerPurchaseOrderNumber: 'PO-PIL-99214',
    orderDate: dateOnlyDaysFromNow(-4),
    shipDate: dateOnlyDaysFromNow(-1),
    lineItems: [
      {
        id: 'demo-soli-1',
        lineItemNumber: 1,
        quantity: 240,
        unitPrice: '420.00',
        lineTotal: '100800.00',
        productName: 'Industrial Hydraulic Pump — HP-480',
        productSku: 'HP-480',
      },
      {
        id: 'demo-soli-2',
        lineItemNumber: 2,
        quantity: 160,
        unitPrice: '230.00',
        lineTotal: '36800.00',
        productName: 'Sealed Bearing Assembly — SBA-22',
        productSku: 'SBA-22',
      },
    ],
    shipments: [
      {
        id: 'demo-shp-1',
        shipDate: dateOnlyDaysFromNow(-1),
        deliveryDate: dateOnlyDaysFromNow(2),
        carrier: 'Old Dominion',
        trackingReference: 'OD-7741209',
        status: 'in_transit',
        quantity: 400,
        weight: '3120.00',
      },
    ],
  },
  salesperson: {
    contactId: 'demo-rep-1',
    name: 'Dana Whitfield',
    email: 'dana.whitfield@gently-demo.com',
  },
  invoiceLineItems: [
    {
      id: 'demo-ili-1',
      lineType: 'product',
      description: 'Industrial Hydraulic Pump — HP-480',
      quantity: '240',
      rate: '420.00',
      amount: '100800.00',
      costBearer: 'customer',
    },
    {
      id: 'demo-ili-2',
      lineType: 'product',
      description: 'Sealed Bearing Assembly — SBA-22',
      quantity: '160',
      rate: '230.00',
      amount: '36800.00',
      costBearer: 'customer',
    },
  ],
  invoiceEvents: [
    {
      id: 'demo-ie-1',
      occurredAt: now,
      actorType: 'system',
      eventType: 'invoice_sent',
      amount: '137600.00',
      note: `Invoice ${INVOICE_NUMBER} sent on branded letterhead`,
      payload: { letterheadUrl: BRAND.letterheadUrl },
    },
  ],
  paymentApplications: [],
  inboundEmails: [],
  outboundEmails: [
    {
      id: 'demo-oe-1',
      subject: `Invoice ${INVOICE_NUMBER} from Gently`,
      from: 'ar@gently-demo.com',
      to: 'ap@harborfoods.com',
      cc: null,
      sentAt: now,
      status: 'sent',
    },
  ],
};

const flagshipInvoiceListItem: ArInvoiceListItem = {
  id: ID.flagshipInvoice,
  invoiceNumber: INVOICE_NUMBER,
  invoiceDate: flagshipInvoice.invoiceDate,
  dueDate: flagshipInvoice.dueDate,
  status: 'open',
  grossAmount: '137600.00',
  netAmount: '137600.00',
  balanceDue: '137600.00',
  currencyCode: 'USD',
  customerName: BRAND.flagshipCustomerName,
  customerId: ID.flagshipCustomer,
  customerPurchaseOrderReference: 'PO-PIL-99214',
  salesOrderId: ID.flagshipSalesOrder,
  shipDate: flagshipInvoice.shipDate,
  paymentApplicationCount: 0,
  firstSentAt: now,
  sendEvents: flagshipInvoice.sendEvents,
};

// ---------------------------------------------------------------------------
// Customer profiles (scenes 3, 7)
// ---------------------------------------------------------------------------
const flagshipProfile: ArCustomerProfileResponse = {
  aging: {
    current: '412800.00',
    days1_30: '0.00',
    days31_60: '0.00',
    days61_90: '0.00',
    days90plus: '0.00',
    totalOpen: '412800.00',
    totalOverdue: '0.00',
    invoiceCount: 3,
  },
  credit: {
    creditLimit: '750000.00',
    utilizationAmount: '412800.00',
    utilizationPct: 55,
    hasLimit: true,
  },
  incomingPayments: { total: '0.00', count: 0 },
  recentPayments: [
    {
      id: 'demo-pay-nw-1',
      paymentDate: dateOnlyDaysFromNow(-22),
      amount: '210000.00',
      referenceNumber: 'WIRE-558201',
      paymentMethod: 'wire',
    },
  ],
  terms: { description: 'Net 30', termDays: 30 },
  contact: {
    contactPerson: 'Marcus Delgado',
    email: 'ap@harborfoods.com',
    phone: '+1 (312) 555-0148',
  },
  behavioral: {
    avgDaysToPay: 27,
    invoiceCountUsed: 14,
    onTimePaymentRate: 93,
    trend: 'stable',
    trendInvoiceCount: 14,
    isNewCustomer: false,
  },
  activityTimeline: [
    {
      id: 'demo-tl-nw-1',
      type: 'invoice_sent',
      timestamp: now,
      description: `Invoice ${INVOICE_NUMBER} sent on branded letterhead`,
      amount: '137600.00',
      status: 'open',
      referenceId: ID.flagshipInvoice,
      referenceType: 'invoice',
      actorUserName: null,
    },
  ],
  inboundEmails: [],
  outboundEmails: [
    {
      id: 'demo-oe-1',
      subject: `Invoice ${INVOICE_NUMBER} from Gently`,
      to: 'ap@harborfoods.com',
      sentAt: now,
    },
  ],
  salesperson: {
    contactId: 'demo-rep-1',
    name: 'Dana Whitfield',
    email: 'dana.whitfield@gently-demo.com',
  },
};

const overdueProfile: ArCustomerProfileResponse = {
  aging: {
    current: '0.00',
    days1_30: '128400.00',
    days31_60: '0.00',
    days61_90: '0.00',
    days90plus: '0.00',
    totalOpen: '128400.00',
    totalOverdue: '128400.00',
    invoiceCount: 2,
  },
  credit: {
    creditLimit: '150000.00',
    utilizationAmount: '128400.00',
    utilizationPct: 86,
    hasLimit: true,
  },
  incomingPayments: { total: '0.00', count: 0 },
  recentPayments: [],
  terms: { description: 'Net 30', termDays: 30 },
  contact: {
    contactPerson: 'Priya Nandakumar',
    email: 'accounts@cascade-mfg.com',
    phone: '+1 (206) 555-0193',
  },
  behavioral: {
    avgDaysToPay: 46,
    invoiceCountUsed: 9,
    onTimePaymentRate: 52,
    trend: 'declining',
    trendInvoiceCount: 9,
    isNewCustomer: false,
  },
  activityTimeline: [
    {
      id: 'demo-tl-cas-1',
      type: 'statement_sent',
      timestamp: isoDaysFromNow(-7),
      description: 'Weekly overdue statement sent',
      amount: '128400.00',
      status: null,
      referenceId: null,
      referenceType: null,
      actorUserName: null,
    },
  ],
  inboundEmails: [],
  outboundEmails: [],
  salesperson: {
    contactId: 'demo-rep-2',
    name: 'Owen Braskey',
    email: 'owen.braskey@gently-demo.com',
  },
};

const disputeProfile: ArCustomerProfileResponse = {
  aging: {
    current: '0.00',
    days1_30: '0.00',
    days31_60: '43250.00',
    days61_90: '0.00',
    days90plus: '0.00',
    totalOpen: '43250.00',
    totalOverdue: '43250.00',
    invoiceCount: 1,
  },
  credit: {
    creditLimit: '200000.00',
    utilizationAmount: '43250.00',
    utilizationPct: 22,
    hasLimit: true,
  },
  incomingPayments: { total: '0.00', count: 0 },
  recentPayments: [
    {
      id: 'demo-pay-gr-1',
      paymentDate: dateOnlyDaysFromNow(-3),
      amount: '43250.00',
      referenceNumber: 'ACH-220194',
      paymentMethod: 'ach',
    },
  ],
  terms: { description: 'Net 45', termDays: 45 },
  contact: {
    contactPerson: 'Helena Fitzroy',
    email: 'ap@granitepeak-dist.com',
    phone: '+1 (303) 555-0177',
  },
  behavioral: {
    avgDaysToPay: 41,
    invoiceCountUsed: 11,
    onTimePaymentRate: 70,
    trend: 'stable',
    trendInvoiceCount: 11,
    isNewCustomer: false,
  },
  activityTimeline: [
    {
      id: 'demo-tl-gr-1',
      type: 'payment_received',
      timestamp: isoDaysFromNow(-3),
      description: 'Short payment received (50% of invoice)',
      amount: '43250.00',
      status: 'short_paid',
      referenceId: null,
      referenceType: 'payment',
      actorUserName: null,
    },
  ],
  inboundEmails: [],
  outboundEmails: [],
  salesperson: null,
};

// ---------------------------------------------------------------------------
// Custom Aging Report dataset (Reports-exploration beat)
//
// Every invoice here is OVERDUE so "show me all overdue invoices, oldest first"
// always reads right (the report's day-range filters are server-side and inert
// in demo mode). The report-only customer "Cedar Ridge Foods" carries four
// invoices that grow in size and lateness — the visible pattern Nami calls out.
// Invoice/due/ship dates all order consistently (older invoice = older on every
// date) so the oldest-first sort is correct under any aging basis.
// ---------------------------------------------------------------------------

/** Build an overdue flat-list invoice row. `daysOverdue` drives all dates. */
function reportInvoice(args: {
  id: string;
  invoiceNumber: string;
  customerId: string;
  customerName: string;
  poRef: string;
  daysOverdue: number;
  termDays: number;
  amount: string;
}): ArInvoiceListItem {
  const dueDate = dateOnlyDaysFromNow(-args.daysOverdue);
  const invoiceDate = dateOnlyDaysFromNow(-(args.daysOverdue + args.termDays));
  const shipDate = dateOnlyDaysFromNow(-(args.daysOverdue + args.termDays - 1));
  const sentAt = isoDaysFromNow(-(args.daysOverdue + args.termDays));
  return {
    id: args.id,
    invoiceNumber: args.invoiceNumber,
    invoiceDate,
    dueDate,
    status: 'open',
    grossAmount: args.amount,
    netAmount: args.amount,
    balanceDue: args.amount,
    currencyCode: 'USD',
    customerName: args.customerName,
    customerId: args.customerId,
    customerPurchaseOrderReference: args.poRef,
    salesOrderId: `${args.id}-so`,
    shipDate,
    paymentApplicationCount: 0,
    firstSentAt: sentAt,
    sendEvents: [
      {
        occurredAt: sentAt,
        note: `Invoice ${args.invoiceNumber} emailed to ${args.customerName}`,
      },
    ],
  };
}

const ID_FILLER_BRIGHTWATER = 'demo-cust-brightwater-0005';
const ID_FILLER_SUMMIT = 'demo-cust-summit-0006';

const reportInvoices: ArInvoiceListItem[] = [
  // Cedar Ridge — four invoices trending larger AND later (the "pattern").
  reportInvoice({
    id: 'demo-inv-cedar-2041',
    invoiceNumber: 'INV-2041',
    customerId: ID.reportCustomer,
    customerName: BRAND.reportCustomerName,
    poRef: 'PO-CRF-2041',
    daysOverdue: 78,
    termDays: 30,
    amount: '18400.00',
  }),
  reportInvoice({
    id: 'demo-inv-cedar-2068',
    invoiceNumber: 'INV-2068',
    customerId: ID.reportCustomer,
    customerName: BRAND.reportCustomerName,
    poRef: 'PO-CRF-2068',
    daysOverdue: 55,
    termDays: 30,
    amount: '22750.00',
  }),
  reportInvoice({
    id: 'demo-inv-cedar-2094',
    invoiceNumber: 'INV-2094',
    customerId: ID.reportCustomer,
    customerName: BRAND.reportCustomerName,
    poRef: 'PO-CRF-2094',
    daysOverdue: 34,
    termDays: 30,
    amount: '31900.00',
  }),
  reportInvoice({
    id: 'demo-inv-cedar-2126',
    invoiceNumber: 'INV-2126',
    customerId: ID.reportCustomer,
    customerName: BRAND.reportCustomerName,
    poRef: 'PO-CRF-2126',
    daysOverdue: 14,
    termDays: 30,
    amount: '41200.00',
  }),
  // Cascade — the 4-weeks-overdue customer ($128,400 across two invoices).
  reportInvoice({
    id: 'demo-inv-cascade-3301',
    invoiceNumber: 'INV-3301',
    customerId: ID.overdueCustomer,
    customerName: BRAND.overdueCustomerName,
    poRef: 'PO-CAS-3301',
    daysOverdue: 28,
    termDays: 30,
    amount: '64200.00',
  }),
  reportInvoice({
    id: 'demo-inv-cascade-3315',
    invoiceNumber: 'INV-3315',
    customerId: ID.overdueCustomer,
    customerName: BRAND.overdueCustomerName,
    poRef: 'PO-CAS-3315',
    daysOverdue: 22,
    termDays: 30,
    amount: '64200.00',
  }),
  // Granite Peak — the short-pay dispute ($43,250 balance on INV-48109).
  reportInvoice({
    id: 'demo-inv-granite-0001',
    invoiceNumber: 'INV-48109',
    customerId: ID.disputeCustomer,
    customerName: BRAND.disputeCustomerName,
    poRef: 'PO-GPD-48109',
    daysOverdue: 5,
    termDays: 45,
    amount: '43250.00',
  }),
  // Filler customers so the report reads as a real book of business.
  reportInvoice({
    id: 'demo-inv-brightwater-7720',
    invoiceNumber: 'INV-7720',
    customerId: ID_FILLER_BRIGHTWATER,
    customerName: 'Brightwater Foods',
    poRef: 'PO-BWF-7720',
    daysOverdue: 41,
    termDays: 30,
    amount: '27500.00',
  }),
  reportInvoice({
    id: 'demo-inv-summit-5108',
    invoiceNumber: 'INV-5108',
    customerId: ID_FILLER_SUMMIT,
    customerName: 'Summit Provisions',
    poRef: 'PO-SMT-5108',
    daysOverdue: 9,
    termDays: 30,
    amount: '12300.00',
  }),
];

/** Grand totals + per-customer grouped rows derived from `reportInvoices`. */
const reportCustomerRows: GetArCustomAgingResponse['customers'] = [
  {
    customerId: ID.reportCustomer,
    customerName: BRAND.reportCustomerName,
    openBalance: '114250.00',
    invoiceCount: 4,
    lastStatementSentAt: isoDaysFromNow(-9),
  },
  {
    customerId: ID.overdueCustomer,
    customerName: BRAND.overdueCustomerName,
    openBalance: '128400.00',
    invoiceCount: 2,
    lastStatementSentAt: isoDaysFromNow(-7),
  },
  {
    customerId: ID.disputeCustomer,
    customerName: BRAND.disputeCustomerName,
    openBalance: '43250.00',
    invoiceCount: 1,
    lastStatementSentAt: isoDaysFromNow(-2),
  },
  {
    customerId: ID_FILLER_BRIGHTWATER,
    customerName: 'Brightwater Foods',
    openBalance: '27500.00',
    invoiceCount: 1,
    lastStatementSentAt: null,
  },
  {
    customerId: ID_FILLER_SUMMIT,
    customerName: 'Summit Provisions',
    openBalance: '12300.00',
    invoiceCount: 1,
    lastStatementSentAt: isoDaysFromNow(-4),
  },
];

const customAging: GetArCustomAgingResponse = {
  customers: reportCustomerRows,
  totals: { openBalance: '325700.00', invoiceCount: reportInvoices.length },
  invoices: reportInvoices,
};

/**
 * Per-customer collection status shown as the chip on every report row. Without
 * this shim the chip defaults to "current" for everyone (the comms hook returns
 * nothing in demo mode), which undercuts the overdue story.
 */
const customerComms: ArCustomerCommsSummary[] = [
  {
    customerId: ID.reportCustomer,
    collectionStatus: 'in_collections',
    collectionStatusUpdatedAt: isoDaysFromNow(-9),
    lastContactAt: isoDaysFromNow(-9),
    recentActivity: [],
  },
  {
    customerId: ID.overdueCustomer,
    collectionStatus: 'promised_to_pay',
    collectionStatusUpdatedAt: isoDaysFromNow(-2),
    lastContactAt: isoDaysFromNow(-2),
    recentActivity: [],
  },
  {
    customerId: ID.disputeCustomer,
    collectionStatus: 'disputed',
    collectionStatusUpdatedAt: isoDaysFromNow(-1),
    lastContactAt: isoDaysFromNow(-1),
    recentActivity: [],
  },
  {
    customerId: ID_FILLER_BRIGHTWATER,
    collectionStatus: 'in_collections',
    collectionStatusUpdatedAt: isoDaysFromNow(-5),
    lastContactAt: isoDaysFromNow(-5),
    recentActivity: [],
  },
  {
    customerId: ID_FILLER_SUMMIT,
    collectionStatus: 'current',
    collectionStatusUpdatedAt: null,
    lastContactAt: isoDaysFromNow(-4),
    recentActivity: [],
  },
];

// ---------------------------------------------------------------------------
// Cedar Ridge Foods profile — the "patterns with this customer" drill-in.
// Declining trend + a new AP contact who joined recently → payments slowing.
// ---------------------------------------------------------------------------
const reportCustomerProfile: ArCustomerProfileResponse = {
  aging: {
    current: '0.00',
    days1_30: '41200.00',
    days31_60: '54650.00',
    days61_90: '18400.00',
    days90plus: '0.00',
    totalOpen: '114250.00',
    totalOverdue: '114250.00',
    invoiceCount: 4,
  },
  credit: {
    creditLimit: '120000.00',
    utilizationAmount: '114250.00',
    utilizationPct: 95,
    hasLimit: true,
  },
  incomingPayments: { total: '0.00', count: 0 },
  recentPayments: [
    {
      id: 'demo-pay-cedar-1',
      paymentDate: dateOnlyDaysFromNow(-96),
      amount: '16200.00',
      referenceNumber: 'CHK-44192',
      paymentMethod: 'check',
    },
  ],
  terms: { description: 'Net 30', termDays: 30 },
  contact: {
    contactPerson: 'Brent Halloway (AP — joined Apr)',
    email: 'ap@cedarridgefoods.com',
    phone: '+1 (503) 555-0162',
  },
  behavioral: {
    // Was a ~26-day payer; now averaging well past terms.
    avgDaysToPay: 58,
    invoiceCountUsed: 12,
    onTimePaymentRate: 33,
    trend: 'declining',
    trendInvoiceCount: 12,
    isNewCustomer: false,
  },
  activityTimeline: [
    {
      id: 'demo-tl-cedar-1',
      type: 'customer_note_added',
      timestamp: isoDaysFromNow(-70),
      description:
        'AP contact changed: Brent Halloway replaced Diane Cofield. Payment cadence has slowed since the handover.',
      amount: null,
      status: null,
      referenceId: null,
      referenceType: null,
      actorUserName: null,
    },
    {
      id: 'demo-tl-cedar-2',
      type: 'statement_sent',
      timestamp: isoDaysFromNow(-9),
      description: 'Overdue statement sent — no response.',
      amount: '114250.00',
      status: null,
      referenceId: null,
      referenceType: null,
      actorUserName: null,
    },
  ],
  inboundEmails: [],
  outboundEmails: [],
  salesperson: { ...REPORT_SALES_REP },
};

const salesRepEmailDraft: DemoSalesRepEmailDraft = {
  customerId: ID.reportCustomer,
  customerName: BRAND.reportCustomerName,
  to: REPORT_SALES_REP.email,
  repName: REPORT_SALES_REP.name,
  subject: `${BRAND.reportCustomerName} — recommend pausing new orders until AR catches up`,
  body: `Hi ${REPORT_SALES_REP.name.split(' ')[0]},\n\nFlagging ${BRAND.reportCustomerName} before we book more volume. Their payment behavior has clearly shifted: average days-to-pay is up to 58 (from ~26), on-time rate has dropped to 33%, and they now have $114,250 overdue across four invoices that are getting both larger and later. The change lines up with a new AP contact, Brent Halloway, taking over in April.\n\nRecommendation: let's hold new orders to ${BRAND.reportCustomerName} until they bring the past-due balance current. I'll keep working collections on our side and update you as soon as it clears.\n\nThanks,\nGently AR`,
  sent: false,
};

// ---------------------------------------------------------------------------
// Collection threads (scenes 7, 10-11)
// ---------------------------------------------------------------------------
const baseThreadFields = {
  resolution: null,
  createdByUserId: null,
  resolvedByUserId: null,
  resolvedAt: null,
  remindAfter: null,
  metadata: {},
} as const;

const disputeThread: CollectionThreadWithActivitiesDto = {
  id: ID.disputeThread,
  referenceNumber: 1024,
  referenceCode: 'COL-1024',
  customerId: ID.disputeCustomer,
  invoiceId: 'demo-inv-granite-0001',
  type: 'short_pay',
  status: 'open',
  ...baseThreadFields,
  title: 'Short pay — 50% withheld, only 10% rejected',
  description:
    'Customer remitted 50% of the invoice citing rejected product, but the BOL shows only 10% was rejected on delivery. Rebuttal email with BOL evidence is drafted and awaiting approval.',
  assigneeUserId: null,
  createdAt: isoDaysFromNow(-3),
  updatedAt: isoDaysFromNow(-1),
  activities: [
    {
      id: 'demo-act-gr-1',
      threadId: ID.disputeThread,
      kind: 'system_event',
      body: 'Short payment detected: $43,250.00 received against $86,500.00 invoice.',
      payload: null,
      actorUserId: null,
      actorName: null,
      createdAt: isoDaysFromNow(-3),
      updatedAt: isoDaysFromNow(-3),
    },
    {
      id: 'demo-act-gr-2',
      threadId: ID.disputeThread,
      kind: 'comment',
      body: 'BOL pulled — only 10% of the shipment was marked rejected on delivery. Rebuttal drafted.',
      payload: null,
      actorUserId: null,
      actorName: 'Gently Agent',
      createdAt: isoDaysFromNow(-1),
      updatedAt: isoDaysFromNow(-1),
    },
  ],
};

const overdueThread: CollectionThreadWithActivitiesDto = {
  id: ID.overdueThread,
  referenceNumber: 1019,
  referenceCode: 'COL-1019',
  customerId: ID.overdueCustomer,
  invoiceId: null,
  type: 'other',
  status: 'open',
  ...baseThreadFields,
  title: '4 weeks overdue — credit hold risk',
  description:
    'Cascade Manufacturing is 28 days past due on $128,400. Recommend an outbound call to warn of a credit hold before escalating.',
  assigneeUserId: null,
  createdAt: isoDaysFromNow(-28),
  updatedAt: isoDaysFromNow(-2),
  activities: [
    {
      id: 'demo-act-cas-1',
      threadId: ID.overdueThread,
      kind: 'system_event',
      body: 'Invoice aged into 1-30 bucket; weekly reminders sent with no response.',
      payload: null,
      actorUserId: null,
      actorName: null,
      createdAt: isoDaysFromNow(-7),
      updatedAt: isoDaysFromNow(-7),
    },
  ],
};

const rankedThreads: RankedCollectionThreadDto[] = [
  {
    ...overdueThread,
    customerName: BRAND.overdueCustomerName,
    dollarsAtRisk: '128400.00',
    daysSinceLastActivity: 2,
    urgencyScore: 92,
  },
  {
    ...disputeThread,
    customerName: BRAND.disputeCustomerName,
    dollarsAtRisk: '43250.00',
    daysSinceLastActivity: 1,
    urgencyScore: 78,
  },
].map(({ activities: _activities, ...rest }) => rest);

const inboxCustomers: InboxCustomerSummaryDto[] = [
  {
    customerId: ID.overdueCustomer,
    customerName: BRAND.overdueCustomerName,
    openCount: 1,
    totalAtRisk: '128400.00',
    oldestOpenedAt: isoDaysFromNow(-28),
    mostRecentActivityAt: isoDaysFromNow(-2),
  },
  {
    customerId: ID.disputeCustomer,
    customerName: BRAND.disputeCustomerName,
    openCount: 1,
    totalAtRisk: '43250.00',
    oldestOpenedAt: isoDaysFromNow(-3),
    mostRecentActivityAt: isoDaysFromNow(-1),
  },
];

const emptyEvidence: ListCollectionThreadEvidenceOutput = {
  paymentApplications: [],
  payments: [],
  remittanceAdvices: [],
  remittanceLineItems: [],
  inboundEmails: [],
  documents: [],
  shipments: [],
  invoice: null,
  salesOrder: null,
  customerPurchaseOrder: null,
  apInvoices: [],
};

const disputeEvidence: ListCollectionThreadEvidenceOutput = {
  ...emptyEvidence,
  payments: [
    {
      id: 'demo-pay-gr-1',
      customerId: ID.disputeCustomer,
      paymentDate: dateOnlyDaysFromNow(-3),
      paymentAmount: '43250.00',
      paymentMethod: 'ach',
      referenceNumber: 'ACH-220194',
      status: 'applied',
    },
  ],
  documents: [
    {
      id: 'demo-doc-bol-1',
      originalFileName: 'BOL-Granite-Peak.pdf',
      status: 'processed',
      classificationType: 'bill_of_lading',
      storagePath: BRAND.bolUrl,
      fileSizeBytes: 184320,
      createdAt: isoDaysFromNow(-2),
    },
  ],
  invoice: {
    id: 'demo-inv-granite-0001',
    internalId: 90211,
    invoiceNumber: 'INV-48109',
    status: 'open',
    invoiceDate: dateOnlyDaysFromNow(-50),
    dueDate: dateOnlyDaysFromNow(-5),
    grossAmount: '86500.00',
    balanceDue: '43250.00',
    currencyCode: 'USD',
  },
};

// ---------------------------------------------------------------------------
// Demo-only narrative state
// ---------------------------------------------------------------------------
// The AR dashboard "Today's work" task list. Ordered top→bottom by what an AR
// clerk should action first on login: money that just arrived, the disputes
// blocking it, then the routine outbound + reconciliation work. The bulk
// statement-send row is spliced in by `buildArDemoSeed` (its count/total are
// computed from the batch). Every row's `toolNames` are eagerly-registered demo
// tools (technical checkpoints + nav in `registerTechnicalDemoTools`, vision
// nav in `useArDemoGlobalNavTools`, bulk in the summary page) so clicking a row
// stands up + opens the same screen the scripted Nami beats do.
const agentActions: DemoAgentAction[] = [
  {
    id: 'demo-action-remittance-crestline',
    kind: 'remittance',
    title: 'Process remittance RA-7741',
    detail:
      'ACH remittance arrived from Crestline Markets — $70,150 across 3 invoices, 1 came in short, plus 1 deduction.',
    customerId: 'crestline',
    customerName: 'Crestline Markets',
    amount: '70150.00',
    status: 'recommended',
    priority: 'high',
    toolNames: [
      'ar_tech_checkpoint_applyRemittanceMatched',
      'ar_tech_navigate_remittance',
    ],
    updatedAt: now,
  },
  {
    id: 'demo-action-shortpay-crestline',
    kind: 'collection_thread',
    title: 'Resolve short-pay on INV-48810',
    detail:
      'Crestline withheld $1,250 with no reason cited — evidence is assembled and ready to rebut.',
    customerId: 'crestline',
    customerName: 'Crestline Markets',
    amount: '1250.00',
    status: 'recommended',
    priority: 'high',
    parentId: 'demo-action-remittance-crestline',
    toolNames: [
      'ar_tech_checkpoint_applyShortPayThreadOpened',
      'ar_tech_navigate_short_pay_thread',
    ],
    updatedAt: now,
  },
  {
    id: 'demo-action-deduction-crestline',
    kind: 'collection_thread',
    title: 'Dispute chargeback #CR-DED-99214',
    detail:
      'Crestline took a $3,100 shortage chargeback — backup lives on their vendor portal.',
    customerId: 'crestline',
    customerName: 'Crestline Markets',
    amount: '3100.00',
    status: 'recommended',
    priority: 'high',
    parentId: 'demo-action-remittance-crestline',
    toolNames: [
      'ar_tech_checkpoint_applyDeductionThreadOpened',
      'ar_tech_navigate_deduction_thread',
    ],
    updatedAt: now,
  },
  {
    id: 'demo-action-rebuttal-granite',
    kind: 'dispute_rebuttal',
    title: `Approve rebuttal to ${BRAND.disputeCustomerName}`,
    detail:
      'Short-pay dispute — BOL shows only 10% rejected. Rebuttal drafted and ready to send.',
    customerId: ID.disputeCustomer,
    customerName: BRAND.disputeCustomerName,
    amount: '43250.00',
    status: 'recommended',
    priority: 'high',
    toolNames: ['ar_navigate_dispute_thread'],
    updatedAt: now,
  },
  {
    id: 'demo-action-reconcile-deposits',
    kind: 'reconciliation',
    title: "Process today's cash receipts",
    detail:
      'Sweep all linked bank accounts — create cash receipts, match remittances, flag short-pays and exceptions.',
    customerId: 'bank',
    customerName: 'All bank accounts',
    amount: null,
    status: 'recommended',
    priority: 'medium',
    toolNames: ['ar_navigate_cash_receipt_ingestion'],
    updatedAt: now,
  },
  {
    id: 'demo-action-call-cascade',
    kind: 'call',
    title: `Call ${BRAND.overdueCustomerName}`,
    detail:
      '28 days overdue on $128,400 — warn of a credit hold before it escalates.',
    customerId: ID.overdueCustomer,
    customerName: BRAND.overdueCustomerName,
    amount: '128400.00',
    status: 'recommended',
    priority: 'medium',
    toolNames: ['ar_navigate_overdue_customer'],
    updatedAt: now,
  },
];

const reminderEmails: DemoReminderEmail[] = Array.from({ length: 5 }).map(
  (_unused, i): DemoReminderEmail => {
    const sentDaysAgo = (i + 1) * 7;
    return {
      id: `demo-reminder-${i + 1}`,
      customerId: ID.overdueCustomer,
      customerName: BRAND.overdueCustomerName,
      to: 'accounts@cascade-mfg.com',
      subject: `Statement of outstanding invoices — week of ${dateOnlyDaysFromNow(-sentDaysAgo)}`,
      body: `Hello ${BRAND.overdueCustomerName} team,\n\nPlease find attached your current statement of outstanding invoices. Overdue items are bolded and highlighted in yellow for your convenience. Kindly arrange payment at your earliest convenience.\n\nThank you,\nGently AR Automation`,
      sentAt: isoDaysFromNow(-sentDaysAgo),
      attachmentUrl: BRAND.highlightedStatementUrl,
      attachmentFileName: `statement-${dateOnlyDaysFromNow(-sentDaysAgo)}.pdf`,
      overdueAmount: '128400.00',
    };
  },
);

// ---------------------------------------------------------------------------
// Scene 7–8 — outbound voice call (recommendation + scripted fallback).
//
// The live call (the demo's one real backend touch) is opt-in and configured
// from the AR module config (`config.ar.demo.voiceConfigId` / `.callTo`), read
// once at startup by the route bootstrap (bootstrap-from-module-config.ts),
// which calls `setDemoLiveCallConfig` below. With neither value set — the safe
// default on stage — the on-screen UI replays `scriptedTranscript`.
//
// We keep the value in a module-level holder (rather than baking it into the
// seed at import time) so the bootstrap can populate it after auth and so a
// one-click reset preserves it.
// ---------------------------------------------------------------------------
let liveCallConfig: {
  voiceAgentConfigId: string | null;
  phoneNumber: string | null;
} = { voiceAgentConfigId: null, phoneNumber: null };

/** Set the live Scene-8 call config (from the AR module config bootstrap). */
export function setDemoLiveCallConfig(config: {
  voiceAgentConfigId: string | null;
  phoneNumber: string | null;
}): void {
  liveCallConfig = config;
}

const CASCADE_FIRST_MESSAGE = `Hi, this is Nami calling on behalf of Pillar, Inc regarding ${BRAND.overdueCustomerName}'s past-due balance. Do you have a moment to talk through invoice payment?`;

// Per-call system prompt for the LIVE Vapi call. Without this the agent follows
// only the Vapi assistant's own (often open-ended) instructions and wanders
// off-topic after the first reply — this keeps it on the collections script and
// makes it hang up once the customer responds.
const CASCADE_SYSTEM_PROMPT = `You are Nami, an accounts-receivable assistant calling on behalf of Pillar, Inc about ${BRAND.overdueCustomerName}'s past-due balance of $128,400 (about four weeks overdue).

Style: Be warm but brisk. State the dollar amount only ONCE in the whole call; after that refer to it as "the balance" or "it" — never repeat the number. Do not recap, summarize, or restate what was already said.

Flow:
1. Greet, say you're calling about the past-due balance for the customer.
2. Tell them amount, that the balance is past due, and the account is approaching a credit hold. Then ask when payment is expected.
3. Ask when payment will be sent.

If they give a date, reply saying you'll email a written confirmation, then end the call.
If they decline to pay, remind them that the account will be placed on a credit hold, and ask them what they'd like to do.

Once they give a date (or decline), reply with ONE sentence confirming the next step, say you'll email a written confirmation, then END the call.

Never discuss anything unrelated to this invoice, never invent account details beyond the amount and that it is ~4 weeks overdue, and never keep talking after they answer your question.`;

const scriptedTranscript: ArDemoFixtureState['voiceCall']['scriptedTranscript'] =
  [
    { speaker: 'agent', text: CASCADE_FIRST_MESSAGE, atMs: 600 },
    {
      speaker: 'customer',
      text: 'Sure, go ahead. What is this about?',
      atMs: 4200,
    },
    {
      speaker: 'agent',
      text: "You have an invoice for $128,400 that's now four weeks past due. I wanted to reach out before the account moves to a credit hold.",
      atMs: 7600,
    },
    {
      speaker: 'customer',
      text: 'A credit hold? We did not realize it was that far behind. We can get a payment out this week.',
      atMs: 12800,
    },
    {
      speaker: 'agent',
      text: "That's great to hear. If payment clears by Friday we'll keep the account in good standing and avoid the hold entirely. I'll send a written confirmation to your accounts inbox.",
      atMs: 17400,
    },
    {
      speaker: 'customer',
      text: "Perfect. Send it over and we'll process it today.",
      atMs: 23000,
    },
    {
      speaker: 'agent',
      text: 'Thank you. Confirmation is on its way — have a great day.',
      atMs: 26200,
    },
  ];

/** Build a fresh, fully-independent copy of the initial demo dataset. */
export function buildArDemoSeed(): ArDemoFixtureState {
  const liveCall = liveCallConfig;
  // AR Technical Demo (F0 / GEN-2256) — the additive Crestline storyline. Built
  // fresh here so a reset re-seeds both storylines to their cold-open state.
  const tech = buildTechnicalSeed();

  // Surface the cash-receipt matcher's remittance advices on the Remittances
  // screen too. De-dupe by reference against the tech advices (RA-7741 is shared
  // with the Crestline storyline) so it isn't listed twice.
  const crRem = buildCashReceiptRemittanceRecords();
  const techRefs = new Set(
    tech.remittanceList
      .map((r) => r.statedReferenceNo)
      .filter((ref): ref is string => ref != null),
  );
  const crRemList = crRem.list.filter(
    (r) => r.statedReferenceNo == null || !techRefs.has(r.statedReferenceNo),
  );
  const crRemById = Object.fromEntries(
    Object.entries(crRem.byId).filter(
      ([, v]) =>
        v.statedReferenceNo == null || !techRefs.has(v.statedReferenceNo),
    ),
  );

  // Bulk statement-send batch (the "approve 25 payment reminders" inbox task).
  // Built here so a reset re-seeds the whole 25-customer batch cold (dialog
  // closed, nothing sent). The matching inbox action is prepended to
  // `agentActions` so it surfaces at the top of the dashboard panel.
  const bulkStatement = buildBulkStatementSeed();
  const bulkStatementAction: DemoAgentAction = {
    id: BULK_STATEMENT_ACTION_ID,
    kind: 'statement',
    title: `Approve ${bulkStatement.customers.length} payment reminders`,
    detail:
      'Weekly run found overdue customers — review and send their outstanding-invoice statements.',
    customerId: 'bulk',
    customerName: `${bulkStatement.customers.length} overdue customers`,
    amount: bulkStatementBatchTotal(bulkStatement),
    status: 'recommended',
    priority: 'medium',
    toolNames: ['ar_open_bulk_statements'],
    updatedAt: now,
  };

  // Fold the 25-customer bulk batch into the aging detail + custom-aging report,
  // then reconcile the summary headline money: the "Total outstanding" card and
  // the aging-detail totals row both read these, so the headline must not read
  // smaller than the (now larger) detail it sits above.
  const agingWithBulk = appendBulkToAging(aging, bulkStatement);
  const customAgingWithBulk = appendBulkToCustomAging(
    customAging,
    bulkStatement,
  );
  const overdueCents = [
    agingWithBulk.totals.days1_30,
    agingWithBulk.totals.days31_60,
    agingWithBulk.totals.days61_90,
    agingWithBulk.totals.days90plus,
  ].reduce((acc, v) => acc + Math.round(parseFloat(v) * 100), 0);
  const reconciledSummary: GetArSummaryResponse = {
    ...summary,
    totalOutstanding: agingWithBulk.totals.totalOutstanding,
    overdueAmount: (overdueCents / 100).toFixed(2),
  };

  const state: ArDemoFixtureState = {
    flagship: {
      customerId: ID.flagshipCustomer,
      customerName: BRAND.flagshipCustomerName,
      invoiceId: ID.flagshipInvoice,
      invoiceNumber: INVOICE_NUMBER,
      salesOrderId: ID.flagshipSalesOrder,
    },
    overdueCustomer: {
      customerId: ID.overdueCustomer,
      customerName: BRAND.overdueCustomerName,
    },
    disputeCustomer: {
      customerId: ID.disputeCustomer,
      customerName: BRAND.disputeCustomerName,
      threadId: ID.disputeThread,
    },
    summary: reconciledSummary,
    // The 25-customer bulk batch is folded into the aging detail (AR summary)
    // and the custom-aging report so the book you can send statements to matches
    // the book you can age. Built above so the summary headline reconciles.
    aging: agingWithBulk,
    customAging: customAgingWithBulk,
    customerComms,
    invoicesById: {
      [ID.flagshipInvoice]: flagshipInvoice,
      ...tech.mergeInto.invoicesById,
    },
    invoiceList: [flagshipInvoiceListItem, ...tech.mergeInto.invoiceList],
    customerProfilesById: {
      [ID.flagshipCustomer]: flagshipProfile,
      [ID.overdueCustomer]: overdueProfile,
      [ID.disputeCustomer]: disputeProfile,
      [ID.reportCustomer]: reportCustomerProfile,
      ...tech.mergeInto.customerProfilesById,
    },
    // Per-customer comms-notes guidance shown on each profile (mirrors the
    // email-intel learnings). Keyed by the same vision/technical customer ids.
    commsNotesByCustomer: buildCommsNotesSeed(),
    rankedThreads,
    inboxCustomers,
    threadsById: {
      [ID.disputeThread]: disputeThread,
      [ID.overdueThread]: overdueThread,
    },
    threadEvidenceById: {
      [ID.disputeThread]: disputeEvidence,
      [ID.overdueThread]: emptyEvidence,
    },
    // Splice the bulk reminder batch in after the high-priority dispute rows
    // (index 4) so it sits with the routine outbound/reconciliation work.
    agentActions: [
      ...agentActions.slice(0, 4),
      bulkStatementAction,
      ...agentActions.slice(4),
    ],
    reminderEmails,
    bulkStatement,
    shippingTerms: {
      incoterm: 'FOB Origin',
      invoiceSendTrigger:
        'Invoice sent the moment the carrier picks up (ship date).',
      invoiceSent: true,
    },
    disputeRebuttal: {
      threadId: ID.disputeThread,
      to: 'ap@granitepeak-dist.com',
      subject: 'Re: Invoice INV-48109 — payment discrepancy',
      body: `Hello ${BRAND.disputeCustomerName} team,\n\nThank you for your payment. Our records show a short payment of $43,250.00 against invoice INV-48109. The attached bill of lading confirms that only 10% of the shipment was recorded as rejected on delivery, not the 50% reflected in the remittance.\n\nWe kindly request remittance of the remaining balance. The supporting BOL is attached for your reference.\n\nBest regards,\nGently AR Automation`,
      bolAttachmentUrl: BRAND.bolUrl,
      bolAttachmentFileName: 'BOL-Granite-Peak.pdf',
      approved: false,
    },
    salesRepEmailDraft,
    workflow: {
      id: 'demo-workflow-weekly-reminder',
      name: 'Weekly overdue-invoice reminders',
      // Plain wording first; scene 5b (ar_highlight_statement) upgrades the
      // description/step/node-subtitle to mention the bold + yellow styling.
      description:
        'Every week, email each customer with unpaid invoices a statement of their outstanding invoices.',
      steps: [
        'Trigger: weekly schedule (Mondays 8:00am)',
        'Find customers with unpaid invoices',
        'Generate statement PDF',
        'Email statement to each customer',
      ],
      // Base graph laid out left-to-right. Scene 5 reveals these; scene 9
      // appends the `added` nodes/edges below them via ar_amend_workflow.
      nodes: [
        {
          id: 'wf-trigger',
          category: 'Trigger',
          stepType: 'schedule_trigger',
          title: 'Weekly schedule',
          subtitle:
            'Runs every Monday at 8:00am to kick off the reminder batch.',
          sockets: { outputs: ['runAt'] },
          position: { x: 0, y: 120 },
        },
        {
          id: 'wf-find-customers',
          category: 'Data Access',
          stepType: 'query_customers',
          title: 'Find customers with unpaid invoices',
          subtitle: 'Queries every customer with an open AR balance above $0.',
          sockets: { inputs: ['runAt'], outputs: ['customers'] },
          position: { x: 320, y: 120 },
        },
        {
          id: 'wf-statement',
          category: 'Data Transform',
          stepType: 'generate_statement',
          title: 'Generate statement PDF',
          subtitle:
            'Builds each customer’s statement of outstanding invoices as a PDF.',
          sockets: { inputs: ['customers'], outputs: ['statementPdf'] },
          position: { x: 720, y: 120 },
        },
        {
          id: 'wf-email',
          category: 'Communications',
          stepType: 'send_email',
          title: 'Email statement to customer',
          subtitle:
            'Sends one reminder email per customer with the PDF attached.',
          sockets: { inputs: ['statementPdf'] },
          position: { x: 1030, y: 120 },
        },
      ],
      edges: [
        {
          id: 'e-trigger-find',
          source: 'wf-trigger',
          target: 'wf-find-customers',
        },
        {
          id: 'e-find-statement',
          source: 'wf-find-customers',
          target: 'wf-statement',
        },
        { id: 'e-statement-email', source: 'wf-statement', target: 'wf-email' },
      ],
      statementHighlighted: false,
      created: false,
      amended: false,
    },
    voiceCall: {
      customerId: ID.overdueCustomer,
      customerName: BRAND.overdueCustomerName,
      displayPhoneNumber: '+1 (415) 555-0147',
      firstMessage: CASCADE_FIRST_MESSAGE,
      systemPrompt: CASCADE_SYSTEM_PROMPT,
      scriptedTranscript,
      scriptedSummary:
        'Reached the accounts team at Cascade Manufacturing. Warned them the $128,400 balance is four weeks past due and heading for a credit hold. They committed to sending payment this week; agreed to send written confirmation. Outcome: payment promised, credit hold avoided.',
      liveVoiceAgentConfigId: liveCall.voiceAgentConfigId,
      livePhoneNumber: liveCall.phoneNumber,
    },

    // ---- AR Technical Demo (F0 / GEN-2256) — additive Crestline slices ----
    // COLD OPEN: the Crestline collection threads are intentionally NOT seeded
    // into threadsById / threadEvidenceById — they are "opened" by the scene
    // checkpoints (short-pay thread at scene 3, deduction thread at scene 6).
    technical: tech.technical,
    remittancesById: { ...tech.remittancesById, ...crRemById },
    remittanceList: [...tech.remittanceList, ...crRemList],
    cashReceiptsById: tech.cashReceiptsById,
    cashReceiptList: tech.cashReceiptList,
    inboundEmailsById: tech.inboundEmailsById,
    contractTerms: tech.contractTerms,
    deductions: tech.deductions,
    bankTransactions: tech.bankTransactions,
    cashReceiptIngestion: buildCashReceiptIngestionSeed(),
    documentPreviewsById: {
      // Vision-demo dispute BOL (opened from the Granite Peak thread evidence).
      'demo-doc-bol-1': buildDocumentPreview({
        fileName: 'BOL — Granite Peak',
        signedUrl: BRAND.bolUrl,
      }),
      ...tech.documentPreviewsById,
    },
    // AR Technical Demo (F2 / GEN-2258) — transient thread-surface UI flags.
    // COLD OPEN: not yet processed, nothing pulled, nothing approved. Reset
    // re-seeds to this.
    technicalUi: {
      remittanceProcessed: false,
      evidencePulled: false,
      rebuttalApproved: false,
    },
    // AR Email Intelligence Demo (E0 / GEN-2262) — cold-open email-intel slice.
    // Participates in reset automatically (resetArDemoState structuredClones
    // buildArDemoSeed()), so "Reset demo" returns it to cold open.
    emailIntel: buildEmailIntelSeed(),
  };

  // Deep clone so callers can mutate freely without touching module state.
  return structuredClone(state);
}
